<template>
	<div class="comment-view">
		<div class="border-2 border-gray-600 rounded-md">
			<span class="type-label text-2xl">{{ comment.type }}</span>
			<div v-if="comment.snippet" class="overflow-auto bg-gray-300 p-2 rounded-md rounded-b-none">
				<pre class="comment-snippet">{{ snippetForInfo(comment.snippet) }}</pre>
			</div>
			<div class="main-section p-2">
				<div v-if="comment.decorations" class="text-sm text-gray-800">
					<span class="bg-gray-300 px-2 py-1 mr-1 rounded-full" v-for="decoration in comment.decorations.split(',')" :key="decoration">{{ decoration.trim() }}</span>
				</div>
				<div v-if="comment.subject" class="markdown-container comment-subject mt-2" v-html="comment.subject"></div>
				<div v-if="comment.discussion" class="markdown-container comment-discussion text-sm mt-2" v-html="comment.discussion"></div>
			</div>
			<div class="text-sm text-right p-1 text-gray-700">
				<span v-if="comment.createdBy" class="mr-1">{{ comment.createdBy }},</span>
				<time :datetime="comment.createdAt">{{ comment.createdAt | relativeTime }}</time>
			</div>
		</div>
		<div class="flex flex-col items-end">
			<div v-if="comment.replies.length" class="max-w-xl w-full mt-2">
				<div v-for="(reply, replyIdx) in comment.replies" class="border-2 border-gray-600 rounded-md my-2" :key="reply.id">
					<span class="reply-index text-gray-800">{{ replyIdx + 1 }}</span>
					<div v-html="reply.content" class="markdown-container p-2"></div>
					<div class="text-sm text-right p-1 text-gray-700">
						<span v-if="reply.createdBy" class="mr-1">{{ reply.createdBy }},</span>
						<time :datetime="comment.createdAt">{{ reply.createdAt | relativeTime }}</time>
					</div>
				</div>
			</div>
			<div class="my-reply max-w-xl text-right w-full mt-2">
				<textarea :ref="`comment-reply-${comment.id}`" class="block w-full"></textarea>
				<button class="mt-1 col-start-2 px-4" @click="onSubmitReply(comment)">Reply</button>
			</div>
		</div>
	</div>
</template>

<script>
import * as marked from 'marked';
import * as DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import { relativeTime } from '@/utils/prettification';
import { mapState } from 'vuex';

export default {
	props: ['comment'],
	computed: {
		...mapState(['username']),
	},
	methods: {
		snippetForInfo(opts) {
			return this.$store.getters.getSnippet({
				trimWhitespace: true,
				...opts,
			});
		},
		onSubmitReply(comment) {
			this.$store.commit('addReply', {
				reply: {
					id: uuidv4(),
					content: DOMPurify.sanitize(marked(this.$refs[`comment-reply-${comment.id}`].value)),
					createdBy: this.username || null,
					createdAt: new Date().toISOString(),
				},
				commentId: comment.id,
			});
			this.$refs[`comment-reply-${comment.id}`].value = '';
		},
	},
	filters: {
		relativeTime,
	},
};
</script>

<style lang="postcss">
.comment-view {
	@apply mt-12;

	.comment-snippet {
		background: transparent;
		padding: 0;
	}
	.type-label {
		position: absolute;
		top: -2.5rem;
	}
	@media (min-width: 860px) {
		@apply mt-4;
		.type-label {
			left: -8px;
			top: 8px;
			transform: translate(-100%, -100%) rotate(-90deg);
			transform-origin: bottom right;
		}
	}
	.reply-index {
		position: absolute;
		right: calc(100% + 12px);
	}
	.main-section > :first-child {
		margin-top: 0;
	}
	.markdown-container {
		> * {
			@apply mt-1;
		}
		h1,
		h2 {
			@apply font-bold mt-4;
		}
		h1 {
			@apply text-3xl;
		}
		h2 {
			@apply text-xl;
		}
		ul {
			@apply list-disc list-inside;
		}
		ol {
			@apply list-decimal list-inside;
		}
		blockquote {
			@apply border-l-2 border-gray-500 pl-2;
			> p {
				@apply whitespace-pre-line;
			}
		}
		code,
		pre {
			@apply bg-gray-300;
			padding: 2px 4px;
			border-radius: 2px;
		}
		pre > code,
		.comment-snippet {
			background: transparent;
			padding: 0;
		}
	}
}
</style>
